import { FunctionComponent, memo, useCallback, useContext } from 'react';
import { UserContext } from '../../../context';
import { StoryCardPostContentBlockFields } from '../../../services';
import {
  AudioBlock,
  BlockTypes,
  CalloutBlock,
  ChecklistBlock,
  CodeBlock,
  EmbedLinkBlock,
  EventBlock,
  FileBlock,
  HeaderBlock,
  HeaderBlockData,
  ImageBlock,
  ImageBlockData,
  ImageBlockSize,
  isFloatedImageBlock,
  ListBlock,
  MentionBlock,
  ParagraphBlock,
  PdfBlock,
  PollBlock,
  QuoteBlock,
  StoryBlock,
  StoryCardBlock,
  TableBlock,
  ThreadBlock,
  ThreadBubble,
  useAnalytics,
  UserBlock,
  VideoBlock,
} from '../../../shared';
import { Audio } from './Audio';
import { Callout } from './Callout';
import { CheckList } from './CheckList';
import { Code } from './Code';
import { Delimiter } from './Delimiter';
import { EmbedLink } from './EmbedLink';
import { EventDetail } from './EventDetail';
import { File } from './File';
import { Header } from './Header';
import { Image } from './Image';
import { List } from './List';
import { Mention } from './Mention';
import { Paragraph } from './Paragraph';
import { Pdf } from './Pdf';
import { PollList } from './PollList';
import { Quote } from './Quote';
import { Story } from './Story';
import { StoryCard } from './StoryCard';
import { Table } from './Table';
import { UserProfile } from './UserProfile';
import { Video } from './Video';

import classNames from 'classnames';
import classes from './DetailsBlock.module.scss';

interface DetailsBlockProps {
  block: StoryCardPostContentBlockFields;
  storyId?: number;
  cardId?: number;
  chatId?: number;
  cardTitle?: string;
}

export const DetailsBlock: FunctionComponent<DetailsBlockProps> = memo(
  ({ block, storyId, cardId, chatId, cardTitle }) => {
    const { logPostOpenLink } = useAnalytics();

    const { channelId } = useContext(UserContext).userInfo.userData;

    const getBlockByType = useCallback(
      (type: string) => {
        switch (type) {
          case BlockTypes.header:
            return <Header {...(block as HeaderBlock).data} />;
          case BlockTypes.paragraph:
            return <Paragraph {...(block as ParagraphBlock).data} />;
          case BlockTypes.list:
            return <List {...(block as ListBlock).data} />;
          case BlockTypes.checklist:
            return <CheckList {...(block as ChecklistBlock).data} />;
          case BlockTypes.embedlink: {
            const { data } = block as EmbedLinkBlock;
            return <EmbedLink storyId={storyId} cardId={cardId} {...data} />;
          }
          case BlockTypes.table:
            return <Table {...(block as TableBlock).data} />;
          case BlockTypes.delimiter:
            return <Delimiter />;
          case BlockTypes.callout:
            return <Callout {...(block as CalloutBlock).data} />;
          case BlockTypes.quote:
            return <Quote {...(block as QuoteBlock).data} />;
          case BlockTypes.image:
            return <Image {...(block as ImageBlock).data} />;
          case BlockTypes.audio: {
            const { id, data } = block as AudioBlock;
            return (
              <Audio
                storyId={storyId}
                cardId={cardId}
                chatId={chatId}
                blockId={id}
                cardTitle={cardTitle}
                {...data}
              />
            );
          }
          case BlockTypes.video: {
            const { id, data } = block as VideoBlock;
            return <Video storyId={storyId} cardId={cardId} blockId={id} {...data} />;
          }
          case BlockTypes.pdf:
            return <Pdf {...(block as PdfBlock).data} />;
          case BlockTypes.file:
            return <File {...(block as FileBlock).data} />;
          case BlockTypes.code:
            return <Code {...(block as CodeBlock).data} />;
          case BlockTypes.user:
            return <UserProfile {...(block as UserBlock).data} />;
          case BlockTypes.story:
            return <Story {...(block as StoryBlock).data} />;
          case BlockTypes.card:
            return <StoryCard {...(block as StoryCardBlock).data} />;
          case BlockTypes.mention:
            return <Mention {...(block as MentionBlock).data} />;
          case BlockTypes.poll:
            return <PollList {...(block as PollBlock).data} />;
          case BlockTypes.event:
            return <EventDetail data={(block as EventBlock).data} />;
          case BlockTypes.thread:
            return <ThreadBubble {...(block as ThreadBlock).data} />;
          default:
            return null;
        }
      },
      [block, cardId, cardTitle, chatId, storyId]
    );

    const getBlockClass = useCallback((block: StoryCardPostContentBlockFields) => {
      const { type, data } = block;

      switch (type) {
        case BlockTypes.paragraph:
          return classNames(classes['block'], classes['block__paragraph']);
        case BlockTypes.header:
          return classNames(
            classes['block'],
            classes[`block__header-${(data as HeaderBlockData).level}`]
          );
        case BlockTypes.list:
        case BlockTypes.checklist:
          return classNames(classes['block'], classes['block__list']);
        case BlockTypes.image: {
          const imageData = data as ImageBlockData;
          const { alignment, size = ImageBlockSize.MEDIUM } = imageData;

          return classNames(classes['block'], {
            [classes[`block--float-${alignment}-${size}`]]: isFloatedImageBlock(imageData),
          });
        }
        default:
          return classes['block'];
      }
    }, []);

    const onBlockClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        const { id, type } = block;

        if (!id) {
          return;
        }

        const blockTypesToTrack = [
          BlockTypes.pdf,
          BlockTypes.file,
          BlockTypes.embedlink,
          BlockTypes.story,
          BlockTypes.card,
          BlockTypes.user,
        ];

        const isLink = (event.target as HTMLElement).tagName.toLowerCase() === 'a';

        if (blockTypesToTrack.includes(type as BlockTypes) || isLink) {
          logPostOpenLink({
            channel_id: channelId,
            story_id: storyId ?? 0,
            item_id: cardId ?? 0,
            block_id: id,
            time_stamp: new Date().toISOString(),
          });
        }
      },
      [block, cardId, channelId, logPostOpenLink, storyId]
    );

    return (
      <div onClick={onBlockClick} className={getBlockClass(block)}>
        {getBlockByType(block.type)}
      </div>
    );
  }
);
